import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isValidIndianMobile, getIndianMobileErrorMessage } from '../utils/validation';
import { contactAPI } from '../services/api';

const Contact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [phoneError, setPhoneError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const prefill = location.state?.contactPrefill;
    if (!prefill) return;
    setFormData((prev) => ({
      ...prev,
      ...(prefill.subject ? { subject: prefill.subject } : {}),
      ...(prefill.message ? { message: prefill.message } : {}),
    }));
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Clear phone error when user starts typing
      if (phoneError) {
        setPhoneError('');
      }
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    // Validate phone number if provided
    if (formData.phone && !isValidIndianMobile(formData.phone)) {
      setPhoneError(getIndianMobileErrorMessage(formData.phone));
      return;
    }

    setIsSubmitting(true);

    try {
      await contactAPI.submit({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject,
        message: formData.message.trim(),
      });

      setSubmitSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setPhoneError('');
    } catch (error) {
      setSubmitError(
        error.response?.data?.error ||
          'Failed to send your message. Please try again or email help@bookforpaws.com directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="bg-[#FAF7F2] md:px-[120px] p-[20px] md:pt-[60px] md:pb-[20px] gap-[16px] flex items-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="hover:bg-gray-100 rounded-full shrink-0"
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clipPath="url(#clip0_contact_back)">
              <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <defs>
              <clipPath id="clip0_contact_back">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
        <h1 className="text-[20px] font-medium text-black tracking-[-1px]">Contact Us</h1>
      </div>

      <div className="md:px-[120px] p-[20px] md:py-[20px] flex flex-col gap-[8px] pb-10 w-full">
        <div className="bg-white rounded-[16px] p-[24px] md:p-[32px] shadow-sm">
          <h2 className="font-medium text-black text-[20px] tracking-[-1px] mb-[16px]">Send us a Message</h2>

          {submitSuccess && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-[14px] text-green-800">
              Thank you for your message! We&apos;ll get back to you soon.
            </div>
          )}

          {submitError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[14px] font-medium text-black mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent text-[14px]"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="block text-[14px] font-medium text-black mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent text-[14px]"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label className="block text-[14px] font-medium text-black mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-[14px] ${
                  phoneError 
                    ? 'border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-[#FF5E4C]'
                }`}
                placeholder="+91 12345 67890"
              />
              {phoneError && (
                <div className="mt-1 text-red-600 text-sm">
                  {phoneError}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[14px] font-medium text-black mb-2">
                Subject *
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent text-[14px]"
              >
                <option value="">Select a subject</option>
                <option value="booking">Booking Inquiry</option>
                <option value="support">Customer Support</option>
                <option value="partnership">Partnership</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-[14px] font-medium text-black mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF5E4C] focus:border-transparent text-[14px]"
                placeholder="Tell us how we can help you..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#FF5E4C] text-white py-3 rounded-lg hover:bg-[#E55347] transition-colors font-medium text-[14px] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact; 